import { useState, useEffect, useCallback, useRef } from 'react';
import { apiClient } from '@/services/api/client';
import { debug } from '@/utils/debug';
import {
  FEEDBACK_CONFIG,
  FEEDBACK_CONFIG_DEFAULTS,
  FREEFORM_QUESTION,
  getOrCreateShuffledSequence,
  getQuestionAtPosition,
  getTimingMs,
  type FeedbackQuestion,
  type FeedbackTimingConfig,
} from '@/config/feedback';
import { useAuthStore } from '@/store/authStore';

const STORAGE_KEYS = FEEDBACK_CONFIG.STORAGE_KEYS;

// Clear stale session data if session is older than 24 hours or sequence is exhausted
function clearStaleSessionData(): void {
  const sessionStart = sessionStorage.getItem(STORAGE_KEYS.SESSION_START);

  // Clear if session is older than 24 hours
  if (sessionStart) {
    const sessionAge = Date.now() - parseInt(sessionStart, 10);
    const maxAge = 24 * 60 * 60 * 1000; // 24 hours
    if (sessionAge > maxAge) {
      debug.log('useFeedback: Clearing stale session data (older than 24h)');
      Object.values(STORAGE_KEYS).forEach((key) => {
        sessionStorage.removeItem(key);
      });
      return;
    }
  }

  // Clear if sequence is exhausted (position beyond available questions)
  const currentPosition = sessionStorage.getItem(STORAGE_KEYS.CURRENT_POSITION);
  const shuffledSequence = sessionStorage.getItem(STORAGE_KEYS.SHUFFLED_SEQUENCE);

  if (currentPosition && shuffledSequence) {
    try {
      const position = parseInt(currentPosition, 10);
      const sequence = JSON.parse(shuffledSequence) as number[];
      // Calculate max positions: sequence length + freeform questions (every 5th)
      const maxPositions = sequence.length + Math.floor(sequence.length / 4);

      if (position > maxPositions || sequence.length === 0) {
        debug.log('useFeedback: Clearing exhausted sequence, resetting to start');
        Object.values(STORAGE_KEYS).forEach((key) => {
          sessionStorage.removeItem(key);
        });
      }
    } catch {
      // Invalid data, clear it
      debug.log('useFeedback: Clearing invalid session data');
      Object.values(STORAGE_KEYS).forEach((key) => {
        sessionStorage.removeItem(key);
      });
    }
  }
}

interface FeedbackState {
  isVisible: boolean;
  currentQuestion: FeedbackQuestion | null;
  isSubmitting: boolean;
  error: string | null;
}

interface UseFeedbackOptions {
  pageContext: 'account' | 'campaign';
  campaignId?: string;
}

interface UseFeedbackReturn extends FeedbackState {
  submitResponse: (response: string) => Promise<boolean>;
}

/**
 * Get cached timing config from session storage or return defaults
 */
function getCachedTimingConfig(): FeedbackTimingConfig {
  const cached = sessionStorage.getItem(STORAGE_KEYS.CONFIG_CACHE);
  if (cached) {
    try {
      return JSON.parse(cached);
    } catch {
      // Invalid cached value, use defaults
    }
  }
  return FEEDBACK_CONFIG_DEFAULTS;
}

/**
 * Hook to manage feedback card state, timing, and submissions
 *
 * Timing logic (configurable via config.json):
 * - Initial delay: Default 10 minutes before first question
 * - After answering: Default 20 minutes cooldown, then show next question
 * - If ignored: Default 5 minutes, then auto-advance to next question
 * - Every 5th position: Show free-form question
 */
export function useFeedback(options: UseFeedbackOptions): UseFeedbackReturn {
  const { isAuthenticated } = useAuthStore();
  const [state, setState] = useState<FeedbackState>({
    isVisible: false,
    currentQuestion: null,
    isSubmitting: false,
    error: null,
  });

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const shuffledSequenceRef = useRef<number[]>([]);
  const timingConfigRef = useRef<FeedbackTimingConfig>(getCachedTimingConfig());
  const configFetchedRef = useRef(false);
  // Ref to store latest checkTiming to avoid stale closure in setInterval
  const checkTimingRef = useRef<(() => void) | null>(null);

  // Fetch timing config from API on mount with AbortController for cleanup
  useEffect(() => {
    if (!isAuthenticated || configFetchedRef.current) return;

    const abortController = new AbortController();

    const fetchConfig = async () => {
      try {
        const response = await apiClient.get('/config/feedback', {
          signal: abortController.signal,
        });
        // Only update state if not aborted
        if (!abortController.signal.aborted) {
          const config = response.data as FeedbackTimingConfig;
          timingConfigRef.current = config;
          sessionStorage.setItem(STORAGE_KEYS.CONFIG_CACHE, JSON.stringify(config));
          configFetchedRef.current = true;
          debug.log('useFeedback: Loaded timing config from API', config);
        }
      } catch (error) {
        // Only log error if not aborted
        if (!abortController.signal.aborted) {
          debug.log('useFeedback: Failed to fetch config, using defaults', error);
          configFetchedRef.current = true;
        }
      }
    };

    fetchConfig();

    return () => {
      abortController.abort();
    };
  }, [isAuthenticated]);

  // Get current position from session storage
  const getCurrentPosition = useCallback((): number => {
    const stored = sessionStorage.getItem(STORAGE_KEYS.CURRENT_POSITION);
    return stored ? parseInt(stored, 10) : 1; // 1-indexed positions
  }, []);

  // Set current position in session storage
  const setCurrentPosition = useCallback((position: number) => {
    sessionStorage.setItem(STORAGE_KEYS.CURRENT_POSITION, position.toString());
  }, []);

  // Get timestamps from session storage
  const getLastShownAt = useCallback((): number | null => {
    const stored = sessionStorage.getItem(STORAGE_KEYS.LAST_SHOWN_AT);
    return stored ? parseInt(stored, 10) : null;
  }, []);

  const getLastAnsweredAt = useCallback((): number | null => {
    const stored = sessionStorage.getItem(STORAGE_KEYS.LAST_ANSWERED_AT);
    return stored ? parseInt(stored, 10) : null;
  }, []);

  const getSessionStart = useCallback((): number => {
    const stored = sessionStorage.getItem(STORAGE_KEYS.SESSION_START);
    if (stored) {
      return parseInt(stored, 10);
    }
    const now = Date.now();
    sessionStorage.setItem(STORAGE_KEYS.SESSION_START, now.toString());
    return now;
  }, []);

  // Show the current question
  const showQuestion = useCallback(() => {
    const position = getCurrentPosition();
    const question = getQuestionAtPosition(position, shuffledSequenceRef.current);

    if (!question) {
      debug.log('useFeedback: No more questions in sequence');
      setState((prev) => ({ ...prev, isVisible: false, currentQuestion: null }));
      return;
    }

    sessionStorage.setItem(STORAGE_KEYS.LAST_SHOWN_AT, Date.now().toString());

    setState((prev) => ({
      ...prev,
      isVisible: true,
      currentQuestion: question,
      error: null,
    }));

    debug.log('useFeedback: Showing question', {
      position,
      questionId: question.id,
      isFreeform: question.id === FREEFORM_QUESTION.id,
    });
  }, [getCurrentPosition]);

  // Advance to next question
  const advanceToNextQuestion = useCallback(() => {
    const currentPosition = getCurrentPosition();
    const nextPosition = currentPosition + 1;
    setCurrentPosition(nextPosition);

    debug.log('useFeedback: Advancing to next question', {
      from: currentPosition,
      to: nextPosition,
    });

    // Show the next question immediately
    const question = getQuestionAtPosition(nextPosition, shuffledSequenceRef.current);
    if (question) {
      sessionStorage.setItem(STORAGE_KEYS.LAST_SHOWN_AT, Date.now().toString());
      setState((prev) => ({
        ...prev,
        isVisible: true,
        currentQuestion: question,
        error: null,
      }));
    } else {
      setState((prev) => ({ ...prev, isVisible: false, currentQuestion: null }));
    }
  }, [getCurrentPosition, setCurrentPosition]);

  // Hide the question (after answering)
  const hideQuestion = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isVisible: false,
      currentQuestion: null,
    }));
  }, []);

  // Check timing and decide what to show
  const checkTiming = useCallback(() => {
    if (!isAuthenticated) return;

    const timing = getTimingMs(timingConfigRef.current);
    const now = Date.now();
    const sessionStart = getSessionStart();
    const lastShownAt = getLastShownAt();
    const lastAnsweredAt = getLastAnsweredAt();

    // If currently visible, check for auto-advance
    if (state.isVisible && lastShownAt) {
      const timeSinceShown = now - lastShownAt;
      if (timeSinceShown >= timing.AUTO_ADVANCE_DELAY_MS) {
        debug.log('useFeedback: Auto-advancing (ignored for configured time)');
        advanceToNextQuestion();
        return;
      }
    }

    // If not visible, check if we should show
    if (!state.isVisible) {
      // If we answered recently, respect the cooldown
      if (lastAnsweredAt) {
        const timeSinceAnswer = now - lastAnsweredAt;
        if (timeSinceAnswer < timing.AFTER_ANSWER_DELAY_MS) {
          debug.log('useFeedback: In cooldown after answer', {
            remaining: Math.round(
              (timing.AFTER_ANSWER_DELAY_MS - timeSinceAnswer) / 1000
            ),
          });
          return;
        }
        // Cooldown passed, show next question
        debug.log('useFeedback: Cooldown passed, showing next question');
        showQuestion();
        return;
      }

      // If we've shown before but haven't answered, check if auto-advance time passed
      if (lastShownAt) {
        const timeSinceShown = now - lastShownAt;
        if (timeSinceShown >= timing.AUTO_ADVANCE_DELAY_MS) {
          debug.log('useFeedback: Auto-advance time passed while not visible, advancing');
          advanceToNextQuestion();
          return;
        }
        // Still within auto-advance window, re-show the current question
        debug.log('useFeedback: Re-showing current question');
        showQuestion();
        return;
      }

      // Initial delay check (first question ever)
      const timeSinceSession = now - sessionStart;
      if (timeSinceSession >= timing.INITIAL_DELAY_MS) {
        debug.log('useFeedback: Initial delay passed, showing first question');
        showQuestion();
      }
    }
  }, [
    isAuthenticated,
    state.isVisible,
    getSessionStart,
    getLastShownAt,
    getLastAnsweredAt,
    showQuestion,
    advanceToNextQuestion,
  ]);

  // Keep checkTimingRef in sync with the latest checkTiming function
  useEffect(() => {
    checkTimingRef.current = checkTiming;
  }, [checkTiming]);

  // Initialize on mount - uses ref to avoid recreating interval on every checkTiming change
  useEffect(() => {
    if (!isAuthenticated) return;

    // Clear any stale session data
    clearStaleSessionData();

    // Initialize shuffled sequence
    shuffledSequenceRef.current = getOrCreateShuffledSequence();

    // Set up periodic check (every 30 seconds) - use ref to always call latest version
    timerRef.current = setInterval(() => {
      checkTimingRef.current?.();
    }, 30 * 1000);

    // Initial check
    checkTimingRef.current?.();

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isAuthenticated]); // Only recreate on auth change, not on checkTiming change

  // Submit feedback response
  const submitResponse = useCallback(
    async (response: string): Promise<boolean> => {
      if (!state.currentQuestion) return false;

      setState((prev) => ({ ...prev, isSubmitting: true, error: null }));

      try {
        await apiClient.post('/feedback/submit', {
          question_id: state.currentQuestion.id,
          question_text: state.currentQuestion.text,
          response,
          response_type: state.currentQuestion.type,
          is_freeform: state.currentQuestion.id === FREEFORM_QUESTION.id,
          page_context: options.pageContext,
          campaign_id: options.campaignId || null,
        });

        debug.log('useFeedback: Response submitted', {
          questionId: state.currentQuestion.id,
        });

        // Record answer time and advance position
        sessionStorage.setItem(STORAGE_KEYS.LAST_ANSWERED_AT, Date.now().toString());
        const currentPosition = getCurrentPosition();
        setCurrentPosition(currentPosition + 1);

        // Hide the card
        hideQuestion();
        setState((prev) => ({ ...prev, isSubmitting: false }));

        return true;
      } catch (error) {
        debug.error('useFeedback: Submit failed', error);
        setState((prev) => ({
          ...prev,
          isSubmitting: false,
          error: 'Failed to submit feedback. Please try again.',
        }));
        return false;
      }
    },
    [state.currentQuestion, options, getCurrentPosition, setCurrentPosition, hideQuestion]
  );

  return {
    ...state,
    submitResponse,
  };
}

/**
 * Initialize feedback session on login
 * Call this when the user logs in to set up the session
 */
export function initializeFeedbackSession(): void {
  // Clear any existing session data
  Object.values(STORAGE_KEYS).forEach((key) => {
    sessionStorage.removeItem(key);
  });

  // Set session start time
  sessionStorage.setItem(STORAGE_KEYS.SESSION_START, Date.now().toString());

  // Generate new shuffled sequence
  getOrCreateShuffledSequence();

  // Start at position 1
  sessionStorage.setItem(STORAGE_KEYS.CURRENT_POSITION, '1');

  debug.log('useFeedback: Session initialized');
}
